import { Member } from '@ui/interfaces'
import axios from '@ui/lib/api/axiosHelper'
import { getQueryRailsHeaders } from './queryRailsUtil'

/*
************************************************************
CONSTANTS
************************************************************
*/
const TEAMS_URL = '/ui/teams'
const MEMBERSHIPS_URL = '/ui/memberships'

/*
************************************************************
TYPES
************************************************************
*/
export interface TeamData {
  status: string
  statusText: string
  data: Team
}

export interface Membership {
  id: number
  status: string
  team_id: string
  user_id: number
  updated_at: string
  created_at: string
}

export interface Team {
  id: string
  name: string
  memberships: Membership[]
}

export interface CreateTeamInput {
  name: string
}

export interface InviteTeamMembersInput {
  user_id: number
  team_id: string
}

interface TeamService {
  getTeams: (member: Member) => Promise<Team[]>
  getTeam: (id: string, member: Member) => Promise<Team>
  createTeam: (data: CreateTeamInput, member: Member) => Promise<TeamData>
  inviteTeamMembers: (data: InviteTeamMembersInput, member: Member) => Promise<TeamData>
  updateTeam: (id: string, data: CreateTeamInput, member: Member) => Promise<TeamData>
  deleteTeam: (id: string, member: Member) => Promise<void>
  removeTeamMember: (id: string, member: Member) => Promise<void>
}

/*
************************************************************
SERVICES
************************************************************
*/
export const teamService: TeamService = {
  getTeams: async (member) => {
    try {
      const response = await axios.get(TEAMS_URL, {
        headers: getQueryRailsHeaders(member),
      })
      return response.data.data
    } catch (error) {
      console.error('Failed to fetch teams', error)
      throw error
    }
  },
  getTeam: async (id, member) => {
    try {
      const response = await axios.get(`${TEAMS_URL}/${id}`, {
        headers: getQueryRailsHeaders(member),
      })
      return response.data.data
    } catch (error) {
      console.error('Failed to fetch team', error)
      throw error
    }
  },
  createTeam: async (data, member) => {
    try {
      const response = await axios.post(
        TEAMS_URL,
        { team: data },
        {
          headers: getQueryRailsHeaders(member),
        }
      )
      return response.data
    } catch (error) {
      console.error('Failed to create team', error)
      throw error
    }
  },
  inviteTeamMembers: async (data, member) => {
    try {
      const response = await axios.post(
        MEMBERSHIPS_URL,
        { membership: data },
        {
          headers: getQueryRailsHeaders(member),
        }
      )
      return response.data
    } catch (error) {
      console.error('Failed to invite team members', error)
      throw error
    }
  },
  updateTeam: async (id, data, member) => {
    try {
      const response = await axios.put(
        `${TEAMS_URL}/${id}`,
        { team: data },
        {
          headers: getQueryRailsHeaders(member),
        }
      )
      return response.data
    } catch (error) {
      console.error('Failed to update team', error)
      throw error
    }
  },

  deleteTeam: async (id, member) => {
    try {
      await axios.delete(`${TEAMS_URL}/${id}`, {
        headers: getQueryRailsHeaders(member),
      })
    } catch (error) {
      console.error('Failed to delete team', error)
      throw error
    }
  },
  removeTeamMember: async (id, member) => {
    try {
      await axios.delete(`${MEMBERSHIPS_URL}/${id}`, {
        headers: getQueryRailsHeaders(member),
      })
    } catch (error) {
      console.error('Failed to remove team member', error)
      throw error
    }
  },
}
